import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { FaEdit, FaRegCalendarAlt, FaTrashAlt, FaTrophy } from "react-icons/fa";
import { BallOutline } from "@/components/icons/BallOutline";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import type { Goalscorer } from "@/components/matches/MatchFormWizard";
import type { MatchRecord } from "@/lib/api/rsvp";

export interface MatchGridCardProps {
  date: Date;
  teamA: number[];
  teamB: number[];
  match: MatchRecord | null;
  goalscorers?: Goalscorer[];
  matchId: number | null;
  missingResult?: boolean;
  isSelected: boolean;
  highlightedPlayerIds: number[];
  getPlayerName: (id: number) => string;
  onSelectChange?: (matchId: number, checked: boolean | string) => void;
  onOpenDetail: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onDismiss?: () => void;
}

function buildGoalMap(goalscorers: Goalscorer[], team: "A" | "B") {
  return goalscorers
    .filter((g) => g.team === team)
    .reduce(
      (acc, g) => {
        acc[g.playerId] = (acc[g.playerId] || 0) + 1;
        return acc;
      },
      {} as Record<number, number>
    );
}

function teamPanelClass(win: boolean, lose: boolean, draw: boolean) {
  if (win) return "bg-green-100/80 dark:bg-green-900/40";
  if (lose) return "bg-red-100/80 dark:bg-red-900/40";
  if (draw) return "bg-yellow-100/80 dark:bg-yellow-900/40";
  return "bg-muted/60";
}

function teamTextClass(win: boolean, lose: boolean, draw: boolean) {
  if (win) return "text-green-900 dark:text-green-100";
  if (lose) return "text-red-900 dark:text-red-100";
  if (draw) return "text-yellow-900 dark:text-yellow-100";
  return "text-muted-foreground";
}

export function MatchGridCard({
  date,
  teamA,
  teamB,
  match,
  goalscorers = [],
  matchId,
  missingResult = false,
  isSelected,
  highlightedPlayerIds,
  getPlayerName,
  onSelectChange,
  onOpenDetail,
  onEdit,
  onDelete,
  onDismiss,
}: MatchGridCardProps) {
  const { t } = useTranslation();

  const isDraw = match?.winner === "Draw";
  const teamAWin = match?.winner === "A";
  const teamBWin = match?.winner === "B";
  const teamALose = match?.winner === "B";
  const teamBLose = match?.winner === "A";
  const formatStr = `${teamA.length}v${teamB.length}`;
  const goalsA = buildGoalMap(goalscorers, "A");
  const goalsB = buildGoalMap(goalscorers, "B");

  const resultLabel = (() => {
    if (!match) return null;
    if (match.scorea != null && match.scoreb != null) {
      return `${match.scorea} - ${match.scoreb}`;
    }
    if (match.winner === "A") return t("matches.teamAWon");
    if (match.winner === "B") return t("matches.teamBWon");
    if (match.winner === "Draw") return t("matches.draw");
    return null;
  })();

  const renderPlayerList = (
    playerIds: number[],
    goals: Record<number, number>,
    win: boolean,
    lose: boolean
  ) => (
    <ul
      className={cn(
        "list-disc list-inside space-y-0.5 pl-3 text-sm",
        teamTextClass(win, lose, isDraw)
      )}
    >
      {playerIds.length === 0 ? (
        <li className="list-none text-xs text-muted-foreground">{t("v2.matches.detail.noResult")}</li>
      ) : (
        playerIds.map((playerId) => {
          const highlighted = highlightedPlayerIds.includes(playerId);
          return (
            <li
              key={playerId}
              className={cn(
                "flex items-center gap-1.5",
                highlighted && "font-bold underline text-blue-700 dark:text-blue-300"
              )}
            >
              <span>{getPlayerName(playerId)}</span>
              {goals[playerId] ? (
                <span className="ml-1 inline-flex items-center gap-0.5 font-bold text-yellow-600">
                  <BallOutline className="h-3.5 w-3.5" />
                  <span className="text-xs">x{goals[playerId]}</span>
                </span>
              ) : null}
            </li>
          );
        })
      )}
    </ul>
  );

  return (
    <article
      className={cn(
        "flex flex-col rounded-xl border bg-card p-3 shadow-sm transition-colors",
        isSelected && "border-soccer-primary/50 ring-1 ring-soccer-primary/30"
      )}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {matchId != null && onSelectChange && (
            <Checkbox
              checked={isSelected}
              onCheckedChange={(checked) => onSelectChange(matchId, checked)}
              onClick={(e) => e.stopPropagation()}
              aria-label={t("matches.selectMatch", "Select match")}
            />
          )}
          <button
            type="button"
            onClick={onOpenDetail}
            className="flex min-w-0 items-center gap-2 text-left"
          >
            <FaRegCalendarAlt className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="truncate text-sm font-semibold">
              {format(date, "MMM d, yyyy")}
            </span>
          </button>
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          {onEdit && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground"
              onClick={onEdit}
              aria-label={t("common.edit")}
            >
              <FaEdit />
            </Button>
          )}
          {onDelete && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground"
              onClick={onDelete}
              aria-label={t("common.delete")}
            >
              <FaTrashAlt />
            </Button>
          )}
          {onDismiss && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground"
              onClick={onDismiss}
              aria-label={t("v2.matches.dismissEvent")}
            >
              <FaTrashAlt />
            </Button>
          )}
        </div>
      </div>

      {missingResult ? (
        <p className="mb-2 rounded-md border border-amber-400/50 bg-amber-400/10 px-2 py-1 text-xs font-medium text-amber-900 dark:text-amber-200">
          {t("v2.matches.missingResultWarning")}
        </p>
      ) : null}

      <button type="button" onClick={onOpenDetail} className="mb-2 flex w-full items-center justify-between text-left">
        <span className="text-sm font-semibold">
          {resultLabel ?? (
            <span className="text-muted-foreground">{t("v2.matches.detail.noResult")}</span>
          )}
        </span>
        {teamA.length > 0 || teamB.length > 0 ? (
          <span className="rounded bg-muted px-2 py-0.5 font-mono text-xs">{formatStr}</span>
        ) : null}
      </button>

      <div className="grid grid-cols-2 gap-2">
        <div className={cn("rounded-lg p-2.5", teamPanelClass(teamAWin, teamALose, isDraw))}>
          <div className="mb-1 flex items-center gap-1.5">
            <FaTrophy
              className={cn(
                "h-3.5 w-3.5",
                teamAWin
                  ? "text-green-500"
                  : teamALose
                    ? "text-red-400"
                    : isDraw
                      ? "text-yellow-500"
                      : "text-muted-foreground"
              )}
            />
            <span className={cn("text-xs font-semibold", teamTextClass(teamAWin, teamALose, isDraw))}>
              {t("matches.teamA", "Equipo A")}
            </span>
          </div>
          {renderPlayerList(teamA, goalsA, teamAWin, teamALose)}
        </div>

        <div className={cn("rounded-lg p-2.5", teamPanelClass(teamBWin, teamBLose, isDraw))}>
          <div className="mb-1 flex items-center gap-1.5">
            <FaTrophy
              className={cn(
                "h-3.5 w-3.5",
                teamBWin
                  ? "text-green-500"
                  : teamBLose
                    ? "text-red-400"
                    : isDraw
                      ? "text-yellow-500"
                      : "text-muted-foreground"
              )}
            />
            <span className={cn("text-xs font-semibold", teamTextClass(teamBWin, teamBLose, isDraw))}>
              {t("matches.teamB", "Equipo B")}
            </span>
          </div>
          {renderPlayerList(teamB, goalsB, teamBWin, teamBLose)}
        </div>
      </div>
    </article>
  );
}
